import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCog,
  faArrowRight,
  faArrowLeft,
  faListAlt,
  faCheckCircle,
  faTimesCircle,
  faCoins,
  faTags,
  faCalendarAlt,
  faBarcode,
} from '@fortawesome/free-solid-svg-icons';
import Pagination from 'react-js-pagination';
import 'animate.css';
import { useTranslation } from 'react-i18next';

import { getRecentTransactions } from '../../services/walletService';
import { getCurrentUser } from '../../services/authService';

import styles from './Transactions.module.css';

function areTxListsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  // Compara campos clave para evitar cambios referenciales que no importan
  for (let i = 0; i < a.length; i++) {
    const A = a[i], B = b[i];
    if (
      A.txid !== B.txid ||
      A.status !== B.status ||
      A.direction !== B.direction ||
      A.amount_satoshis !== B.amount_satoshis ||
      A.fee_satoshis !== B.fee_satoshis ||
      A.created_at !== B.created_at
    ) return false;
  }
  return true;
}

const Transactions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // Guarda el último estado por txid para decidir si animar o no
  const prevStatusRef = useRef({});

  useEffect(() => {
    const user = getCurrentUser();
    if (!user) {
      navigate('/login');
      return;
    }

    const fetchTransactions = async () => {
      try {
        const result = await getRecentTransactions();
        const incoming = result?.data?.transactions || [];
        // Evita refrescos innecesarios
        setTransactions(prev => (areTxListsEqual(prev, incoming) ? prev : incoming));
      } catch (error) {
        console.error('Error fetching transactions:', error);
      }
    };

    fetchTransactions();
    const intervalId = setInterval(fetchTransactions, 5000);
    return () => clearInterval(intervalId);
  }, [navigate]);

  // Actualiza el mapa de estados previos (se hace después de render)
  useEffect(() => {
    const map = { ...prevStatusRef.current };
    for (const tx of transactions) {
      map[tx.txid] = tx.status;
    }
    prevStatusRef.current = map;
  }, [transactions]);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const current = transactions.slice(indexOfFirst, indexOfLast);

  const formatAmt = (sats) => (Number(sats || 0) / 100000000).toFixed(8);
  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso || ''; }
  };

  const StatusBadge = ({ status, animate }) => {
    // Sólo animamos si realmente cambió el estado
    const anim = animate ? 'animate__animated animate__fadeIn' : '';
    if (status === 'confirmed') {
      return (
        <span className={`${styles.badgeOk} ${anim}`}>
          <FontAwesomeIcon icon={faCheckCircle} />
          {t('confirmed')}
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className={`${styles.badgeDanger} ${anim}`}>
          <FontAwesomeIcon icon={faTimesCircle} />
          {t('failed')}
        </span>
      );
    }
    return (
      <span className={`${styles.badgePending}`}> {/* sin animación para evitar parpadeo */}
        <FontAwesomeIcon icon={faCog} spin />
        {t('pending')}
      </span>
    );
  };

  const DirectionPill = ({ direction }) =>
    direction === 'incoming' ? (
      <span className={styles.pillIn}>
        <FontAwesomeIcon icon={faArrowLeft} />
        {t('received')}
      </span>
    ) : (
      <span className={styles.pillOut}>
        <FontAwesomeIcon icon={faArrowRight} />
        {t('sent')}
      </span>
    );

  return (
    <div className={`${styles.root} animate__animated animate__fadeIn`}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <FontAwesomeIcon icon={faListAlt} /> {t('recent_transactions')}
        </h1>
        <p className={styles.subtitle}>{t('last_operations')}</p>
      </header>

      <div className={styles.tableShell}>
        <div className={styles.tableHead} role="row">
          <div className={styles.th}>
            <FontAwesomeIcon icon={faCoins} className={styles.thIcon} />
            {t('amount')}
          </div>
          <div className={styles.th}>
            <FontAwesomeIcon icon={faCoins} className={styles.thIcon} />
            {t('fee')}
          </div>
          <div className={styles.th}>
            <FontAwesomeIcon icon={faTags} className={styles.thIcon} />
            {t('type')}
          </div>
          <div className={styles.th}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.thIcon} />
            {t('status')}
          </div>
          <div className={styles.th}>
            <FontAwesomeIcon icon={faCalendarAlt} className={styles.thIcon} />
            {t('date')}
          </div>
          <div className={styles.th}>
            <FontAwesomeIcon icon={faBarcode} className={styles.thIcon} />
            {t('txid')}
          </div>
        </div>

        <div className={styles.tableBody}>
          {current.length > 0 ? (
            current.map((tx) => {
              const prev = prevStatusRef.current[tx.txid];
              const statusChanged = prev && prev !== tx.status;

              return (
                <div
                  key={tx.txid} // clave estable evita re-montajes
                  className={`${styles.tr} ${
                    tx.direction === 'incoming' ? styles.rowIncoming : styles.rowOutgoing
                  }`}
                  role="row"
                >
                  <div className={styles.td} data-label={t('amount')}>
                    <span className={styles.mono}>{formatAmt(tx.amount_satoshis)} MENEL</span>
                  </div>

                  <div className={styles.td} data-label={t('fee')}>
                    <span className={styles.mono}>{formatAmt(tx.fee_satoshis)} MENEL</span>
                  </div>

                  <div className={styles.td} data-label={t('type')}>
                    <DirectionPill direction={tx.direction} />
                  </div>

                  <div className={styles.td} data-label={t('status')}>
                    <StatusBadge status={tx.status} animate={statusChanged} />
                  </div>

                  <div className={styles.td} data-label={t('date')}>
                    <span className={styles.muted}>{formatDate(tx.created_at)}</span>
                  </div>

                  <div className={styles.td} data-label={t('txid')}>
                    <a
                      href={`https://explorer.menelik.cc/txid/${tx.txid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.txidLink}
                      title={tx.txid}
                    >
                      <span className={styles.txidText}>{tx.txid}</span>
                    </a>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyCard}>
                <div className={styles.emptyIcon}>
                  <FontAwesomeIcon icon={faListAlt} />
                </div>
                <p className={styles.emptyText}>{t('no_transactions')}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.paginationWrap}>
        <Pagination
          activePage={currentPage}
          itemsCountPerPage={itemsPerPage}
          totalItemsCount={transactions.length}
          pageRangeDisplayed={5}
          onChange={handlePageChange}
          itemClass={`page-item ${styles.pageItem}`}
          linkClass={`page-link ${styles.pageLink}`}
          activeClass={`active ${styles.pageActive}`}
        />
      </div>
    </div>
  );
};

export default Transactions;
